/**
 * Fase 1F — Cliente API global multi-tenant.
 *
 * Prefixo automático ``/platform`` para TODAS as chamadas quando a
 * feature flag está ativa, evitando editar dezenas de telas
 * operacionais. Também instala o refresh single-flight com limite de
 * uma repetição.
 *
 * O baseURL do cliente Axios é ``${REACT_APP_BACKEND_URL}/api``.
 * Portanto, quando reescrevermos ``/auth/login`` para
 * ``/platform/auth/login``, a URL final é
 * ``${REACT_APP_BACKEND_URL}/api/platform/auth/login`` — sem duplicar
 * ``/api``.
 */
import { isPlatformAuthEnabled } from "./flags";

const PATHS_KEPT_UNPREFIXED = new Set([
  "/health",
  "/healthz",
]);

// Endpoints que NUNCA devem disparar refresh (evita loop de 401).
const REFRESH_EXCLUDED_URL_PARTS = [
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
  "/auth/select-tenant",
  "/auth/accept-invite",
];

/**
 * Reescreve o `config.url` para o prefixo global quando aplicável.
 * - Se já começa com "/platform/", mantém.
 * - Se é URL absoluta (http/https), mantém.
 * - Se está em ``PATHS_KEPT_UNPREFIXED``, mantém.
 * - Caso contrário, prepend "/platform".
 */
export function rewritePathForPlatform(url) {
  if (!url || typeof url !== "string") return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/platform/")) return url;
  // remove barra inicial dupla, se houver
  const clean = url.startsWith("/") ? url : `/${url}`;
  if (PATHS_KEPT_UNPREFIXED.has(clean)) return url;
  return `/platform${clean}`;
}

export function isRefreshEligible(url) {
  if (!url) return false;
  return !REFRESH_EXCLUDED_URL_PARTS.some((p) => url.includes(p));
}

// Fase 1L.8C — endpoint dinâmico de refresh. AuthContext troca entre
// ``/platform/auth/refresh`` (tenant) e ``/platform/auth/refresh/admin-console``
// (Admin Console) conforme o estado atual. Nenhum token é lido/escrito
// pelo JavaScript — só a URL do POST muda.
let _platformRefreshEndpoint = "/platform/auth/refresh";
export function setPlatformRefreshEndpoint(url) {
  if (typeof url === "string" && url.startsWith("/platform/auth/refresh")) {
    _platformRefreshEndpoint = url;
  } else {
    _platformRefreshEndpoint = "/platform/auth/refresh";
  }
}
export function getPlatformRefreshEndpoint() {
  return _platformRefreshEndpoint;
}

// Fase 1L.8C — guarda de boot. Enquanto ``true``, o handler de
// ``onUnauthorized`` fica silenciado para evitar flash de GUEST antes
// da tentativa de fallback para o Admin Console.
let _platformBooting = false;
export function setPlatformBootingFlag(flag) {
  _platformBooting = !!flag;
}

/**
 * Instala interceptors no cliente Axios existente. Chamado UMA vez
 * durante a inicialização (feito por ``src/lib/api.js``).
 *
 * @param {import('axios').AxiosInstance} client
 * @param {{ onUnauthorized?: () => void }} opts
 */
export function installPlatformInterceptors(client, opts = {}) {
  const onUnauthorized = opts.onUnauthorized || (() => {});

  // Request: reescreve URL para /platform/*
  client.interceptors.request.use((config) => {
    if (isPlatformAuthEnabled()) {
      config.url = rewritePathForPlatform(config.url);
      config.withCredentials = true;
    }
    return config;
  });

  // Response: refresh single-flight + limite de UMA repetição.
  let refreshInFlight = null;
  client.interceptors.response.use(
    (r) => r,
    async (error) => {
      if (!isPlatformAuthEnabled()) return Promise.reject(error);
      const status = error?.response?.status;
      const original = error?.config;
      if (status !== 401 || !original) return Promise.reject(error);
      if (original._platformRetried) return Promise.reject(error);
      if (!isRefreshEligible(original.url)) return Promise.reject(error);

      // Single-flight: várias requisições concorrentes aguardam o mesmo refresh.
      if (!refreshInFlight) {
        refreshInFlight = client
          .post(_platformRefreshEndpoint)
          .then(() => true)
          .catch(() => false)
          .finally(() => {
            // libera o slot APÓS todos os aguardadores lerem o resultado
            setTimeout(() => { refreshInFlight = null; }, 0);
          });
      }
      const ok = await refreshInFlight;
      if (!ok) {
        if (!_platformBooting) {
          try { onUnauthorized(); } catch { /* noop */ }
        }
        return Promise.reject(error);
      }
      original._platformRetried = true;
      return client.request(original);
    },
  );
}
